import ClientForm, {
  ClientFormHandle,
  ClientFormValues,
} from 'components/featured/Client/ClientForm/ClientForm';
import ClientFormPage from 'components/featured/Client/ClientFormPage';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { CreateClientV2Payload } from 'model/client.model';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RoutePaths from 'routes/RoutePaths';
import { HuddleClientService } from 'service/huddle-client.service';

const ClientNew = () => {
  const navigate = useNavigate();
  const clientFormRef = useRef<ClientFormHandle>(null);
  const [formValues, setFormValues] = useState<ClientFormValues | null>(null);
  const [isValid, setIsValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { alertState, completeOperation, setAlertState, getAlertContent } = useAlertStatus();

  const handleChange = (values: ClientFormValues, valid: boolean) => {
    setFormValues(values);
    setIsValid(valid);
  };

  const handleSubmit = async () => {
    if (!formValues || !isValid) return;
    setSubmitting(true);
    try {
      const profileImageUrl = (await clientFormRef.current?.uploadImage()) ?? null;
      const organizationId = sessionStorage.getItem('organizationId') || undefined;
      const PRODUCTION_TYPE = 'production co';
      const type = formValues.clientType?.toLocaleLowerCase() || '';
      const payload: CreateClientV2Payload = {
        name: formValues.name,
        organizationId,
        profileImageUrl: profileImageUrl || null,
        clientType: type === PRODUCTION_TYPE ? PRODUCTION_TYPE.replace(' ', '_') : type,
        salutation: formValues.salutation || undefined,
        league: formValues.league || undefined,
        classification: formValues.league ? [formValues.league] : undefined,
        position: formValues.position || undefined,
        agency: formValues.agency || undefined,
        email: formValues.email || undefined,
        personEmail: formValues.personalEmail || undefined,
        personMobilePhone: formValues.mobile || undefined,
        phone: formValues.phone || undefined,
        team: formValues.team || undefined,
        college: formValues.college || undefined,
        status: formValues.status || undefined,
        number: formValues.number || undefined,
        bio: formValues.bio || undefined,
        ...((formValues.billingStreet ||
          formValues.billingCity ||
          formValues.billingState ||
          formValues.billingCountry ||
          formValues.billingZip) && {
          billingAddress: {
            street: formValues.billingStreet || undefined,
            city: formValues.billingCity || undefined,
            state: formValues.billingState || undefined,
            country: formValues.billingCountry || undefined,
            postalCode: formValues.billingZip || undefined,
          },
        }),
        ...((formValues.mailingStreet ||
          formValues.mailingCity ||
          formValues.mailingState ||
          formValues.mailingCountry ||
          formValues.mailingZip) && {
          personMailingAddress: {
            street: formValues.mailingStreet || undefined,
            city: formValues.mailingCity || undefined,
            state: formValues.mailingState || undefined,
            country: formValues.mailingCountry || undefined,
            postalCode: formValues.mailingZip || undefined,
          },
        }),
        ownerName: formValues.owner || undefined,
        parentClientId: formValues.linkEntity?.[0] || undefined,
        enterpriseName: formValues.loanOutCompany || undefined,
        loanOutCompany: formValues.loanOutCompany || undefined,
      };
      const res = await HuddleClientService.createClientV2(payload);
      const newId = res?.clientId || res?.recordId || '';
      sessionStorage.setItem('new-client', newId);
      sessionStorage.setItem('platform', 'huddle');
      navigate(RoutePaths.Client, { replace: true });
    } catch (err: any) {
      completeOperation('create', err?.message ?? 'Failed to create client', true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ClientFormPage
        isEdit={false}
        onSubmit={handleSubmit}
        submitting={submitting}
        primaryLabel={isValid ? 'Create Client' : undefined}
        primaryDisabled={!isValid}
      >
        <ClientForm ref={clientFormRef} onChange={handleChange} />
      </ClientFormPage>
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </>
  );
};

export default ClientNew;
